import React, { useState } from 'react';
import { ChakraFabButton } from 'app/components/Core';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { ContentContainerType } from 'types/enums';
import { Song } from 'types/song';
import { AppDispatch, useAppSelector } from 'app/store';
import { clickUploadButtonAction } from 'app/store/Mixpanel/slice';
import { ChakraEmptyList } from 'app/components/Core/ChakraEmptyList';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { getStorage } from 'app/store/Storage/selectors';
import { AudioTableList } from 'app/components/Song/Audio/AudioTableList';
import { ReactComponent as AudioIcon } from 'resources/images/audio.svg';
import { AudioFormValidationProperties, AudioTypesDDL } from 'types/audio';
import { uploadAudioBlockAction } from 'app/store/SongList/slice';
import { useStorageFeatureFlag } from 'featureFlags';
import { setShowUpgradePopup } from 'app/store/Storage/slice';
import { AudioGroupedList } from './types';
import { AudioAddDialog } from '../AudioAddDialog';
import { AudioDragAndDropModal } from '../AudioDragAndDropModal';

export interface AudioListComponentProps {
  loading: boolean;
  createAudioHref?: string;
  deleteAudioHref?: string;
  dispatch: AppDispatch;
  audioFormValidation?: AudioFormValidationProperties;
  audioList?: AudioGroupedList;
  audioTypes: AudioTypesDDL;
  downloadAudioHref?: string;
  isShareAudioEnabled: boolean;
  song?: Song;
}

export const AudioListComponent: React.FC<AudioListComponentProps> = React.memo(
  ({
    loading,
    createAudioHref,
    dispatch,
    audioFormValidation,
    audioList,
    audioTypes,
    downloadAudioHref,
    deleteAudioHref,
    isShareAudioEnabled,
    song,
  }) => {
    const storage = useAppSelector(getStorage);
    const [audioFiles, setAudioFiles] = useState<FileList>();
    const [showAddAudioDialog, setShowAddAudioDialog] = useState(false);
    const showEmptyState = !loading && audioList?.length === 0;
    const [dndModalVisible, setDndModalVisible] = useState(false);
    const { storageFeatureEnabled } = useStorageFeatureFlag();

    const onUploadClick = () => {
      dispatch(clickUploadButtonAction());
      if (
        storageFeatureEnabled &&
        storage?.enforceLimit &&
        storage?.freeBytes !== undefined &&
        storage?.freeBytes <= 0
      ) {
        dispatch(uploadAudioBlockAction());
        dispatch(setShowUpgradePopup({ messageType: 'storage' }));
        return;
      }
      setDndModalVisible(true);
    };

    const onStorageLimitReached = () => {
      if (storageFeatureEnabled) {
        dispatch(setShowUpgradePopup({ messageType: 'storage' }));
      }
    };

    return (
      <ContentContainer
        type={ContentContainerType.LIST}
        isViewContent
        centerContent={showEmptyState}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.dataTransfer.types.includes('Files')) {
            setDndModalVisible(true);
          }
        }}
      >
        {showEmptyState ? (
          <ChakraEmptyList
            icon={<AudioIcon role="img" />}
            title="Upload a Recording"
            primaryText="Record and capture song ideas using the Session app, or upload a file from your computer to playback and safely share audio."
          />
        ) : (
          <>
            {audioList && (
              <AudioTableList
                loading={loading}
                audioList={audioList}
                song={song}
                audioTypes={audioTypes}
                dispatch={dispatch}
                deleteAudioHref={deleteAudioHref}
                downloadAudioHref={downloadAudioHref}
                isShareAudioEnabled={isShareAudioEnabled}
                audioFormValidation={audioFormValidation}
              />
            )}
          </>
        )}
        <ChakraFabButton
          isDisabled={!createAudioHref || !audioFormValidation}
          aria-label="upload audio"
          data-testid="upload-audio-button"
          onClick={onUploadClick}
          leftIcon={<AddIcon />}
        >
          Upload audio
        </ChakraFabButton>
        {audioFormValidation && (
          <AudioDragAndDropModal
            isOpen={dndModalVisible}
            close={() => {
              setDndModalVisible(false);
            }}
            setAudioFiles={setAudioFiles}
            dispatch={dispatch}
            setDnDModal={setDndModalVisible}
            setShowAddAudioDialog={setShowAddAudioDialog}
            setShowStorageOverLimitDialog={onStorageLimitReached}
            audioFormValidation={audioFormValidation}
          />
        )}
        {audioFormValidation && (
          <AudioAddDialog
            open={showAddAudioDialog}
            handleClose={() => setShowAddAudioDialog(false)}
            setAudioFiles={setAudioFiles}
            audioFiles={audioFiles}
            audioFormValidation={audioFormValidation}
            audioTypes={audioTypes}
            dispatch={dispatch}
          />
        )}
      </ContentContainer>
    );
  }
);
